import { useCallback, useSyncExternalStore } from 'react';
import {
  defaultCommunityListFilters,
  type CommunityListFilters,
} from './propertyUtils';

export type CommunitySort = 'featured' | 'price-asc' | 'price-desc' | 'name-asc' | 'distance';
export type MobileBrowseView = 'map' | 'list';

export interface CommunitiesBrowseState {
  filters: CommunityListFilters;
  sort: CommunitySort;
  selectedId: string | null;
  focusNonce: number;
  focusSource: 'map' | 'list';
  mobileView: MobileBrowseView;
  mapExpanded: boolean;
  compareIds: string[];
  userLocation: { lat: number; lng: number } | null;
  filteredIds: string[];
  resultCount: number;
  resultsReady: boolean;
  toast: string | null;
}

const CHANGE_EVENT = 'al-communities-browse-change';

let state: CommunitiesBrowseState = {
  filters: defaultCommunityListFilters(),
  sort: 'featured',
  selectedId: null,
  focusNonce: 0,
  focusSource: 'list',
  mobileView: 'list',
  mapExpanded: false,
  compareIds: [],
  userLocation: null,
  filteredIds: [],
  resultCount: 0,
  resultsReady: false,
  toast: null,
};

let toastTimer: ReturnType<typeof setTimeout> | null = null;

function emit() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(CHANGE_EVENT));
  }
}

function setState(partial: Partial<CommunitiesBrowseState>) {
  state = { ...state, ...partial };
  emit();
}

function subscribe(onStoreChange: () => void) {
  const handler = () => onStoreChange();
  window.addEventListener(CHANGE_EVENT, handler);
  return () => window.removeEventListener(CHANGE_EVENT, handler);
}

function getSnapshot() {
  return state;
}

function getServerSnapshot(): CommunitiesBrowseState {
  return state;
}

export function useCommunitiesBrowse(): CommunitiesBrowseState {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

export function setBrowseFilters(filters: CommunityListFilters) {
  setState({ filters });
}

export function setBrowseSort(sort: CommunitySort) {
  setState({ sort });
}

export function setBrowseResults(filteredIds: string[], resultCount: number) {
  if (
    state.resultsReady &&
    state.resultCount === resultCount &&
    state.filteredIds.length === filteredIds.length &&
    state.filteredIds.every((id, index) => id === filteredIds[index])
  ) {
    return;
  }
  setState({ filteredIds, resultCount, resultsReady: true });
}

type SelectOptions = { focus?: boolean; source?: 'map' | 'list' };

export function selectCommunity(id: string | null, opts?: SelectOptions) {
  if (!opts?.focus && state.selectedId === id) return;
  setState({
    selectedId: id,
    focusNonce: opts?.focus ? state.focusNonce + 1 : state.focusNonce,
    focusSource: opts?.focus ? opts.source ?? 'list' : state.focusSource,
  });
}

export function setMobileBrowseView(mobileView: MobileBrowseView) {
  setState({ mobileView });
}

export function setMapExpanded(mapExpanded: boolean) {
  setState({ mapExpanded });
}

export function setUserLocation(userLocation: { lat: number; lng: number } | null) {
  setState({ userLocation, sort: userLocation ? 'distance' : state.sort });
}

export function toggleCompareId(id: string) {
  if (!id) return;
  const exists = state.compareIds.includes(id);
  let compareIds: string[];
  if (exists) {
    compareIds = state.compareIds.filter((item) => item !== id);
  } else if (state.compareIds.length >= 3) {
    showBrowseToast('Compare up to 3 communities');
    return;
  } else {
    compareIds = [...state.compareIds, id];
  }
  setState({ compareIds });
}

export function clearCompare() {
  setState({ compareIds: [] });
}

export function showBrowseToast(toast: string) {
  if (toastTimer) clearTimeout(toastTimer);
  setState({ toast });
  toastTimer = setTimeout(() => {
    setState({ toast: null });
    toastTimer = null;
  }, 2600);
}

export function clearBrowseToast() {
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = null;
  setState({ toast: null });
}

export function useBrowseActions() {
  return {
    setFilters: setBrowseFilters,
    setSort: setBrowseSort,
    setResults: setBrowseResults,
    select: selectCommunity,
    setMobileView: setMobileBrowseView,
    setMapExpanded,
    setUserLocation,
    toggleCompare: toggleCompareId,
    clearCompare,
    showToast: showBrowseToast,
    clearToast: clearBrowseToast,
  };
}

export function useSelectCommunity() {
  return useCallback((id: string | null, opts?: SelectOptions) => {
    selectCommunity(id, opts);
  }, []);
}
